import { subDays, startOfDay, format, parseISO } from "date-fns";
import type { InstallPeriodType, InstallRecordType } from "@intelligentlilli/api-layer";
import theme from "../../Styles/theme.scss";

// --- Presets ---

export type PresetKey =
  | "last7days"
  | "last4weeks"
  | "last3months"
  | "last6months"
  | "last12months"
  | "custom";

export type Granularity = "D" | "W" | "M";

export interface Preset {
  key: PresetKey;
  label: string;
  defaultGranularity: Granularity;
  days: number;
}

export const PRESETS: Preset[] = [
  { key: "last7days", label: "Last 7 days", defaultGranularity: "D", days: 7 },
  { key: "last4weeks", label: "Last 4 weeks", defaultGranularity: "W", days: 28 },
  { key: "last3months", label: "Last 3 months", defaultGranularity: "M", days: 90 },
  { key: "last6months", label: "Last 6 months", defaultGranularity: "M", days: 180 },
  { key: "last12months", label: "Last 12 months", defaultGranularity: "M", days: 365 },
  { key: "custom", label: "Custom range", defaultGranularity: "D", days: 0 },
];

export const PRESET_OPTIONS = PRESETS.map((p) => ({
  label: p.label,
  value: p.key,
}));

// --- Date range computation ---

export const getPresetDateRange = (
  presetKey: PresetKey,
  customRange?: { startDate: Date; endDate: Date } | null
): { startTime: string; endTime: string } => {
  const now = new Date();
  const endTime = now.toISOString();

  if (presetKey === "custom" && customRange) {
    return {
      startTime: startOfDay(customRange.startDate).toISOString(),
      endTime: customRange.endDate.toISOString(),
    };
  }

  const preset = PRESETS.find((p) => p.key === presetKey);
  const days = preset?.days || 7;
  const startTime = startOfDay(subDays(now, days)).toISOString();

  return { startTime, endTime };
};

// --- Cache key ---

export const getCacheKey = (
  orgId: number,
  granularity: Granularity,
  startTime: string,
  endTime: string
): string => `${orgId}-${granularity}-${startTime}-${endTime}`;

// --- Data transforms ---

export const flattenFailedRecords = (
  periods: InstallPeriodType[]
): InstallRecordType[] => {
  const records = periods.flatMap((period) => period.records.failed);
  return records.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
};

// --- Sorting ---

type SortField = "userName" | "timestamp" | "reason";
type SortDirection = "asc" | "desc";

export const sortRecords = (
  records: InstallRecordType[],
  field: SortField,
  direction: SortDirection
): InstallRecordType[] => {
  const sorted = [...records].sort((a, b) => {
    const valA = a[field] ?? "";
    const valB = b[field] ?? "";

    if (field === "timestamp") {
      return new Date(valA).getTime() - new Date(valB).getTime();
    }

    return String(valA).toLowerCase().localeCompare(String(valB).toLowerCase());
  });

  return direction === "desc" ? sorted.reverse() : sorted;
};

// --- Badge colors ---

interface BadgeStyle {
  backgroundColor: string;
  color: string;
}

const REASON_STYLES: Record<string, BadgeStyle> = {
  refused: { backgroundColor: theme.risk2, color: theme.risk6 },
  unavailable: { backgroundColor: theme.unexpected1, color: theme.neutral7 },
  unsafe: { backgroundColor: theme.risk1, color: theme.risk5 },
  logistics: { backgroundColor: theme.primary1, color: theme.primary6 },
};

const UNKNOWN_STYLE: BadgeStyle = {
  backgroundColor: theme.neutral2,
  color: theme.neutral6,
};

export const getReasonBadgeStyle = (reason: string | null): BadgeStyle => {
  if (!reason) return UNKNOWN_STYLE;
  return REASON_STYLES[reason.toLowerCase()] || UNKNOWN_STYLE;
};

export const getReasonLabel = (reason: string | null): string => {
  if (!reason) return "Unknown";
  return reason.charAt(0).toUpperCase() + reason.slice(1).toLowerCase();
};

// --- Safe date formatting ---

export const safeFormatISO = (isoString: string, formatStr: string): string => {
  try {
    const date = parseISO(isoString);
    if (isNaN(date.getTime())) return isoString;
    return format(date, formatStr);
  } catch {
    return isoString;
  }
};

// --- Chart label formatting ---

export const formatPeriodLabel = (
  period: InstallPeriodType,
  granularity: Granularity
): string => {
  switch (granularity) {
    case "D":
      return safeFormatISO(period.period.start, "EEE d");
    case "W":
      return `W/C ${safeFormatISO(period.period.start, "d MMM")}`;
    case "M":
      return safeFormatISO(period.period.start, "MMM yyyy");
    default:
      return period.key;
  }
};

// --- Range label formatting ---

export const formatRangeLabel = (
  range: { start: string; end: string },
  granularity: Granularity
): string => {
  switch (granularity) {
    case "D":
      return `${safeFormatISO(range.start, "d MMM yyyy")} – ${safeFormatISO(range.end, "d MMM yyyy")}`;
    case "W":
      return `${safeFormatISO(range.start, "d MMM")} – ${safeFormatISO(range.end, "d MMM yyyy")}`;
    case "M":
      return `${safeFormatISO(range.start, "MMM yyyy")} – ${safeFormatISO(range.end, "MMM yyyy")}`;
    default:
      return `${safeFormatISO(range.start, "d MMM yyyy")} – ${safeFormatISO(range.end, "d MMM yyyy")}`;
  }
};
